import React from 'react';
import LogoVariations from '../../components/base/LogoVariations';

export default function LogoPreview() {
  const sections = [
    {
      title: 'Current Logo',
      variants: ['current' as const]
    },
    {
      title: 'Minimalist Monogram',
      description: 'Interlocking letters in geometric designs',
      variants: ['monogram-1' as const, 'monogram-2' as const]
    },
    {
      title: 'Abstract Circle Symbol',
      description: 'Complete circle with symbolic elements',
      variants: ['circle-1' as const, 'circle-2' as const]
    },
    {
      title: 'Elegant Wordmark',
      description: 'Pure sophistication through typography',
      variants: ['wordmark-1' as const, 'wordmark-2' as const]
    },
    {
      title: 'Shield/Crest Design',
      description: 'Traditional emblem conveying trust and heritage',
      variants: ['shield-1' as const, 'shield-2' as const]
    },
    {
      title: 'Geometric Pattern',
      description: 'Overlapping shapes representing interconnected services',
      variants: ['geometric-1' as const, 'geometric-2' as const]
    },
    {
      title: 'Infinity Symbol',
      description: 'Modified loop suggesting continuous support',
      variants: ['infinity-1' as const, 'infinity-2' as const]
    },
    {
      title: 'Compass Rose',
      description: 'Navigation metaphor for life guidance',
      variants: ['compass-1' as const, 'compass-2' as const]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-50 to-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-serif text-slate-900 mb-3">Logo Variations</h1>
          <p className="text-lg text-slate-600">Explore different design directions for Full Circle Assist</p>
        </div>
      </div>

      {/* Logo Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="space-y-20">
          {sections.map((section, idx) => (
            <div key={idx}>
              <div className="mb-8">
                <h2 className="text-2xl font-serif text-slate-900 mb-2">{section.title}</h2>
                {section.description && (
                  <p className="text-slate-600">{section.description}</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-8">
                {section.variants.map((variant, vIdx) => (
                  <div key={vIdx} className="space-y-6">
                    {/* Light Background */}
                    <div className="border border-slate-200 rounded-xl p-12 bg-white">
                      <div className="flex items-center justify-center">
                        <LogoVariations variant={variant} />
                      </div>
                      <p className="text-center text-sm text-slate-500 mt-6">Light background</p>
                    </div>

                    {/* Dark Background */}
                    <div className="border border-slate-800 rounded-xl p-12 bg-slate-900">
                      <div className="flex items-center justify-center">
                        <LogoVariations 
                          variant={variant} 
                          textColor="#FFFFFF"
                          accentColor="#C8A97E"
                        />
                      </div>
                      <p className="text-center text-sm text-slate-400 mt-6">Dark background</p>
                    </div>

                    {/* Colored Background */}
                    <div className="border border-amber-200 rounded-xl p-12 bg-gradient-to-br from-amber-50 to-orange-50">
                      <div className="flex items-center justify-center">
                        <LogoVariations 
                          variant={variant}
                          textColor="#2D2A26"
                          accentColor="#B8860B"
                        />
                      </div>
                      <p className="text-center text-sm text-amber-700 mt-6">Colored background</p>
                    </div>
                  </div>
                ))}
              </div>

              {idx < sections.length - 1 && (
                <div className="mt-16 border-t border-slate-200"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="bg-white border border-slate-200 rounded-xl p-8">
            <h3 className="text-lg font-serif text-slate-900 mb-3">Next Steps</h3>
            <p className="text-slate-600 mb-4">
              Review each variation and let me know which direction resonates with you. I can refine any of these designs or create additional variations based on your preferences.
            </p>
            <p className="text-sm text-slate-500">
              Consider factors like: versatility across different sizes, memorability, alignment with your brand values, and how it appears on various backgrounds.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
